/**
 * Email Verification Page — shown when client hasn't verified their email
 */

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';

// After a send, or a "too many attempts" answer, the Resend button waits this
// long. 2026-10-26: a double-tap fired two sends 0.2 s apart; Firebase Auth
// refused the second, which paged as an error and showed the client "internal".
const RESEND_COOLDOWN_SECONDS = 60;
const SEND_FAILED_MESSAGE = 'Could not send the email right now. Please try again in a minute.';

export function VerifyEmail() {
  const { user, signOut, resendVerification } = useAuth();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // A ref, not state: two taps in the same frame would both read the old state.
  const inFlight = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (inFlight.current || cooldown > 0) return;
    inFlight.current = true;
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      await resendVerification();
      setSent(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setSent(false);
      const code = (err as { code?: unknown } | null)?.code;
      const message = err instanceof Error ? err.message : '';
      if (code === 'functions/resource-exhausted') {
        // Not the client's fault and not a failure: an email was just sent.
        // A notice in the server's own words, not a red error.
        setNotice(message || 'Please wait a few minutes, then try again.');
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        // A bare "internal" tells the client nothing.
        setError(!message || /^internal$/i.test(message) ? SEND_FAILED_MESSAGE : message);
      }
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  const handleRefresh = async () => {
    // Sign out so user re-authenticates with fresh token (emailVerified updated)
    await signOut();
    window.location.href = '/login';
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <img src="/logo.png" alt="JAD Enterprises" className="login-logo" />
        <h1>Verify Your Email</h1>
        <p style={{ color: 'var(--gray-400)', marginBottom: '20px', textAlign: 'center' }}>
          A verification email was sent to <strong style={{ color: 'var(--white)' }}>{user?.email}</strong>.
          Please check your inbox and click the verification link.
        </p>

        {sent && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
            background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981',
            fontSize: '0.85rem', textAlign: 'center',
          }}>Verification email sent. Check your inbox.</div>
        )}

        {notice && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
            background: 'rgba(200,168,78,0.1)', border: '1px solid rgba(200,168,78,0.35)', color: '#c8a84e',
            fontSize: '0.85rem', textAlign: 'center',
          }}>{notice}</div>
        )}

        {error && (
          <div className="error-message">{error}</div>
        )}

        <button onClick={handleRefresh} className="btn btn-primary" style={{ width: '100%', marginBottom: '10px' }}>
          I've Verified My Email
        </button>
        <button
          onClick={handleResend}
          disabled={sending || cooldown > 0}
          className="btn btn-secondary"
          style={{ width: '100%', marginBottom: '10px' }}
        >
          {sending ? 'Sending…' : cooldown > 0 ? `Resend available in ${cooldown}s` : 'Resend Verification Email'}
        </button>
        <button onClick={signOut} className="btn btn-secondary" style={{ width: '100%' }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}
